import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Sparkles,
  Video,
  PenSquare,
  ArrowRight,
  AlertTriangle,
  XOctagon,
  Copy,
  Check,
  ExternalLink,
  MessageSquareReply,
} from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button, Textarea } from "@/components/ui";
import { toast } from "sonner";
import { ReviewServices } from "@/services/bedrock";
import { useComplianceChecker } from "@/utils";
import { Review } from "@/services/bedrock";
import { socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { ReviewResponseRefinement } from "./ReviewResponseRefinement";
import { cn } from "@/lib/utils";

interface ReviewReplyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: Review;
  generatedText: string;
  isGenerating: boolean;
  mode: "video" | "text";
  onContinueToTeleprompter?: (script: string) => void;
  onTextReplyComplete?: () => void;
  /** When set in text mode, shows a bottom-left action to switch into the video reply flow. */
  onReplyWithVideo?: () => void;
  /** When set in video mode, shows a bottom-left action to switch back to the text reply flow. */
  onReplyWithText?: () => void;
}

const LOADING_PHRASES = [
  "Polishing the hooks...",
  "Analyzing your tone adjustments...",
  "Tailoring the response to your review...",
  "Formatting for perfect delivery...",
  "Reviewing compliance guidelines...",
  "Adding some SocialCoach magic...",
  "Ensuring everything sounds natural...",
  "Crafting a friendly call-to-action...",
  "Perfecting your response tone...",
];

export function ReviewReplyDialog({
  open,
  onOpenChange,
  review,
  generatedText,
  isGenerating,
  mode,
  onContinueToTeleprompter,
  onTextReplyComplete,
  onReplyWithVideo,
  onReplyWithText,
}: ReviewReplyDialogProps) {
  const [editedText, setEditedText] = useState(generatedText);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);

  const lastSyncedText = useRef("");

  // Reset sync when switching text/video so the newly generated content replaces the editor.
  useEffect(() => {
    lastSyncedText.current = "";
  }, [mode]);

  // Sync edited text when generated text arrives
  useEffect(() => {
    if (generatedText && !isGenerating && generatedText !== lastSyncedText.current) {
      setEditedText(generatedText);
      lastSyncedText.current = generatedText;
    }
  }, [generatedText, isGenerating]);

  // Handle rotating loading phrases
  const isLoadingAny = isGenerating || isRefining || isSubmitting;

  useEffect(() => {
    if (!isLoadingAny) return;
    const interval = setInterval(() => {
      setPhraseIndex(prev => (prev + 1) % LOADING_PHRASES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isLoadingAny]);

  // Compliance checker
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker();

  const blockedWords = useMemo(() => findBlockedWords(editedText || ""), [editedText, findBlockedWords]);
  const flaggedWords = useMemo(() => findFlaggedWords(editedText || ""), [editedText, findFlaggedWords]);

  const hasBlockedWords = blockedWords.length > 0;

  const platformName = review
    ? socialNetworkNames[review.sourceSystem as SocialNetworksId] || review.sourceSystem
    : "Platform";
  const isZillow = review?.sourceSystem === "ZL";

  // Handle Text Reply Posting or Copy & Go
  const handleTextReplyAction = async () => {
    if (!editedText.trim()) return;

    if (isZillow) {
      // Copy & Go to Public URL Flow (For Zillow, which doesn't support API reply)
      navigator.clipboard.writeText(editedText);
      setCopied(true);
      toast.success("Response copied to clipboard! Redirecting to Zillow...");

      setIsSubmitting(true);
      try {
        // Even for Zillow, we want to mark it as replied in our system
        await ReviewServices.updateReview(review.id, { replyStatus: "replied" });

        setTimeout(() => {
          setCopied(false);
          const publicUrl = review.socialIdentity?.publicUrl;
          if (publicUrl) {
            window.open(publicUrl, "_blank");
          }
          onOpenChange(false);
          if (onTextReplyComplete) onTextReplyComplete();
        }, 1000);
      } catch (error) {
        console.error("Failed to mark Zillow review as replied:", error);
        toast.error("Failed to update reply status.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // In-Platform Direct Response Flow (Google, Facebook, etc.)
      setIsSubmitting(true);
      try {
        await ReviewServices.submitReviewReply(review.id, { message: editedText });
        toast.success(`Reply posted successfully to ${platformName}!`);
        onOpenChange(false);
        if (onTextReplyComplete) onTextReplyComplete();
      } catch (error) {
        console.error("Failed to post reply:", error);
        toast.error(`Failed to post reply to ${platformName}.`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={"2xl"}>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-xl font-extrabold text-slate-800">
          {mode === "video" ? (
            <>
              <Sparkles className="size-5 text-primary animate-pulse" />
              Review Your Script
            </>
          ) : (
            <>
              <MessageSquareReply className="size-5 text-primary animate-pulse" />
              Reply on {platformName}
            </>
          )}
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="max-h-[65vh] overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Left Column: Script/Text Editor & Compliance */}
          <div className="md:col-span-6 flex flex-col gap-2">
            <label className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <PenSquare className="size-3.5" />
              {mode === "video" ? "Teleprompter Script" : "Text Response"}
            </label>

            <div className="relative flex-1 flex flex-col">
              <Textarea
                value={editedText}
                onChange={(e: any) => setEditedText(e.target.value)}
                disabled={isLoadingAny}
                className="min-h-[280px] flex-1 text-sm leading-relaxed p-4 bg-white border-slate-300 hover:border-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg shadow-sm resize-none font-medium text-slate-800 transition-all focus-visible:ring-0 focus-visible:ring-offset-0 outline-none"
                placeholder={
                  mode === "video" ? "Your video script will appear here..." : "Your AI text reply will appear here..."
                }
              />
              {isLoadingAny && (
                <div className="absolute inset-0 bg-white/85 backdrop-blur-[2px] flex flex-col items-center justify-center rounded-lg border border-slate-200 z-20 transition-all duration-300">
                  <div className="relative flex items-center justify-center mb-4">
                    <div className="absolute size-14 rounded-full border-4 border-indigo-100 animate-ping" />
                    <div className="size-12 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
                    <Sparkles className="absolute size-5 text-indigo-500 animate-pulse" />
                  </div>

                  <div className="text-center px-6 max-w-xs">
                    <h4 className="text-sm font-extrabold text-indigo-950 uppercase tracking-widest mb-1.5 animate-pulse">
                      {isSubmitting ? (
                        "Posting Reply..."
                      ) : (
                        <img alt={"SocialCoach AI"} src="https://cdn.socialcoach.io/socoai_mono_positive.svg" />
                      )}
                    </h4>
                    <p className="text-xs font-semibold text-slate-500 min-h-[16px] transition-all duration-500 ease-in-out">
                      {isSubmitting ? "Uploading response to profile..." : LOADING_PHRASES[phraseIndex]}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Compliance Alerts */}
            {hasBlockedWords && (
              <div className="mt-2 flex items-start gap-2.5 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs font-medium leading-relaxed shadow-sm">
                <XOctagon className="size-4 text-rose-600 shrink-0 mt-0.5 animate-bounce" />
                <div>
                  <span className="font-extrabold block uppercase tracking-wider text-rose-900 mb-0.5">
                    Blocked Content Detected
                  </span>
                  Your response contains compliance-blocked terms:{" "}
                  <span className="font-extrabold underline">{blockedWords.join(", ")}</span>. Please remove these terms
                  before continuing.
                </div>
              </div>
            )}

            {flaggedWords.length > 0 && !hasBlockedWords && (
              <div className="mt-2 flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs font-medium leading-relaxed shadow-sm">
                <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
                <div>
                  <span className="font-extrabold block uppercase tracking-wider text-amber-900 mb-0.5">
                    Compliance Warning
                  </span>
                  Your response contains flagged keywords:{" "}
                  <span className="font-bold underline">{flaggedWords.join(", ")}</span>. You may want to revise them to
                  stay aligned with brand guidelines.
                </div>
              </div>
            )}
          </div>

          {/* Right Column: AI Refinement Options */}
          <div className="md:col-span-6 border-t md:border-t-0 md:border-l border-slate-100 pt-6 md:pt-0 md:pl-6">
            <ReviewResponseRefinement
              reviewId={review.id}
              mode={mode}
              currentResponse={editedText}
              onRefined={setEditedText}
              onRefining={setIsRefining}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Footer
        className={cn(
          ((mode === "text" && onReplyWithVideo) || (mode === "video" && onReplyWithText)) && "sm:justify-between"
        )}
      >
        {mode === "text" && onReplyWithVideo ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onReplyWithVideo}
            disabled={isLoadingAny}
            className="text-slate-500 font-semibold gap-1.5 sm:mr-auto"
          >
            <Video className="size-4" />
            Reply with Video
          </Button>
        ) : mode === "video" && onReplyWithText ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onReplyWithText}
            disabled={isLoadingAny}
            className="text-slate-500 font-semibold gap-1.5 sm:mr-auto"
          >
            <MessageSquareReply className="size-4" />
            Reply with Text
          </Button>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500 font-semibold">
            Cancel
          </Button>

          {mode === "video" ? (
            <Button
              onClick={() => onContinueToTeleprompter?.(editedText)}
              disabled={isLoadingAny || !editedText.trim() || hasBlockedWords}
              className="font-bold flex items-center gap-2 h-10 px-6"
            >
              <Video className="size-4" />
              Continue to Teleprompter
              <ArrowRight className="size-4 ml-1" />
            </Button>
          ) : isZillow ? (
            <Button
              onClick={handleTextReplyAction}
              disabled={isLoadingAny || !editedText.trim() || hasBlockedWords}
              className="font-bold flex items-center gap-2 h-10 px-6"
            >
              {copied ? (
                <>
                  <Check className="size-4" />
                  Copied! Redirecting...
                </>
              ) : (
                <>
                  <Copy className="size-4" />
                  Copy & Go to Zillow
                  <ExternalLink className="size-4 ml-1" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleTextReplyAction}
              disabled={isLoadingAny || !editedText.trim() || hasBlockedWords}
              className="font-bold flex items-center gap-2 h-10 px-6"
            >
              <Check className="size-4" />
              Post Reply to {platformName}
            </Button>
          )}
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
